import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    ArrowDown, ArrowUp, BookmarkPlus, Copy, Eye, EyeOff, History, Layers, Monitor, Plus, Power, Redo2, RotateCcw,
    Save, Smartphone, Tablet, Trash2, Undo2, Upload, X,
} from "lucide-react";
import { rhm, apiError } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Btn, Card, Field, Input, PageHead, Select, Textarea } from "../ui";
import { REGISTRY, blankSection } from "../../site/registry";
import { Inspector } from "./websiteFields";
import { AddSection, InlineEditor, PublishDialog, SaveTemplateDialog, VersionHistory } from "./websiteDialogs";
import { FooterEditor, HeaderEditor } from "./websiteGlobals";
import { BannerManager } from "./websiteBanners";
import { PagesManager } from "./websitePages";
import { CollectionsManager } from "./websiteCollections";
import { ProductPagesManager } from "./websiteProductPages";
import { useSaveShortcut, saveKeyLabel } from "../useSaveShortcut";

const DEVICES = [
    { key: "desktop", label: "Desktop", icon: Monitor, width: "100%" },
    { key: "tablet", label: "Tablet", icon: Tablet, width: "834px" },
    { key: "mobile", label: "Mobile", icon: Smartphone, width: "390px" },
];
const TABS = [
    { key: "layout", label: "PAGE LAYOUT" },
    { key: "header", label: "HEADER & MENU" },
    { key: "footer", label: "FOOTER" },
    { key: "pages", label: "PAGES" },
    { key: "shop", label: "SHOP COLLECTIONS" },
    { key: "product_pages", label: "PRODUCT PAGES" },
    { key: "banners", label: "BANNERS" },
];

export default function RhmWebsite() {
    const { user, can } = useRhmAuth();
    const qc = useQueryClient();
    const iframeRef = useRef(null);
    const [tab, setTab] = useState("layout");
    const [slug, setSlug] = useState("home");
    const [globals, setGlobals] = useState(null);
    const [globalsDirty, setGlobalsDirty] = useState(false);
    const [device, setDevice] = useState("desktop");
    const [sections, setSections] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [history, setHistory] = useState([]);
    const [future, setFuture] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [inline, setInline] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [saveTemplateFor, setSaveTemplateFor] = useState(null);
    const [showPublish, setShowPublish] = useState(false);
    const [showVersions, setShowVersions] = useState(false);

    const editable = can("website.edit");
    const { data: settings } = useQuery({
        queryKey: ["site-settings"],
        queryFn: async () => (await rhm.get("/site/settings")).data,
    });
    const { data: page } = useQuery({
        queryKey: ["site-page", slug],
        queryFn: async () => (await rhm.get(`/site/pages/${slug}`)).data,
    });
    const { data: pages = [] } = useQuery({
        queryKey: ["site-pages"],
        queryFn: async () => (await rhm.get("/site/pages")).data,
    });
    const { data: globalsDoc } = useQuery({
        queryKey: ["site-globals-crm"],
        queryFn: async () => (await rhm.get("/site/globals")).data,
    });
    const { data: products = [] } = useQuery({
        queryKey: ["site-products"],
        queryFn: async () => (await rhm.get("/products")).data,
    });

    useEffect(() => { setSections(null); setSelectedId(null); setDirty(false); }, [slug]);
    useEffect(() => {
        if (page && page.slug === slug && sections === null) setSections(page.draft?.sections || []);
    }, [page, sections, slug]);
    useEffect(() => { if (globalsDoc && globals === null) setGlobals(globalsDoc.draft); }, [globalsDoc, globals]);

    const post = useCallback((list, sel, glob) => {
        const frame = iframeRef.current?.contentWindow;
        if (!frame) return;
        frame.postMessage({ type: "rhm-layout", sections: list, selectedId: sel }, "*");
        if (glob) frame.postMessage({ type: "rhm-globals", globals: glob }, "*");
    }, []);

    useEffect(() => { if (sections) post(sections, selectedId, globals); }, [sections, selectedId, globals, post]);

    useEffect(() => {
        const onMessage = (e) => {
            const msg = e.data || {};
            if (msg.type === "rhm-preview-ready") post(sections || [], selectedId, globals);
            if (msg.type === "rhm-select") setSelectedId(msg.id);
            if (msg.type === "rhm-edit-text") { setSelectedId(msg.payload.sectionId); setInline(msg.payload); }
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
    }, [sections, selectedId, globals, post]);

    const commit = (next) => {
        setHistory((h) => [...h.slice(-40), sections]);
        setFuture([]);
        setSections(next);
        setDirty(true);
    };
    const undo = () => {
        if (!history.length) return;
        setFuture((f) => [sections, ...f]);
        setSections(history[history.length - 1]);
        setHistory((h) => h.slice(0, -1));
        setDirty(true);
    };
    const redo = () => {
        if (!future.length) return;
        setHistory((h) => [...h, sections]);
        setSections(future[0]);
        setFuture((f) => f.slice(1));
        setDirty(true);
    };

    const selected = useMemo(() => (sections || []).find((s) => s.id === selectedId) || null, [sections, selectedId]);
    const setProp = (key, value) => commit((sections || []).map((s) =>
        s.id === selectedId ? { ...s, props: { ...(s.props || {}), [key]: value } } : s));
    const setSection = (patch) => commit((sections || []).map((s) => (s.id === selectedId ? { ...s, ...patch } : s)));

    const move = (id, dir) => {
        const list = [...sections];
        const i = list.findIndex((s) => s.id === id);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= list.length) return;
        [list[i], list[j]] = [list[j], list[i]];
        commit(list);
    };
    const drop = (fromId, toId) => {
        if (fromId === toId) return;
        const list = [...sections];
        const from = list.findIndex((s) => s.id === fromId);
        const to = list.findIndex((s) => s.id === toId);
        const [moved] = list.splice(from, 1);
        list.splice(to, 0, moved);
        commit(list);
    };
    const duplicate = (id) => {
        const list = [...sections];
        const i = list.findIndex((s) => s.id === id);
        const copy = JSON.parse(JSON.stringify(list[i]));
        copy.id = `${copy.type}_${Math.random().toString(36).slice(2, 8)}`;
        copy.name = `${copy.name} copy`;
        list.splice(i + 1, 0, copy);
        commit(list);
        setSelectedId(copy.id);
    };
    const removeSection = (id) => {
        if (!can("website.sections_delete")) return toast.error("You do not have permission to delete sections");
        commit(sections.filter((s) => s.id !== id));
        if (selectedId === id) setSelectedId(null);
    };
    const toggleVisible = (id) => {
        if (!can("website.sections_visibility")) return toast.error("You do not have permission to hide sections");
        commit(sections.map((s) => (s.id === id ? { ...s, visible: s.visible === false } : s)));
    };
    const addSection = (type) => {
        const s = blankSection(type);
        commit([...(sections || []), s]);
        setSelectedId(s.id);
        setShowAdd(false);
    };
    const addFromTemplate = (t) => {
        const s = {
            id: `${t.section_type}_${Math.random().toString(36).slice(2, 8)}`,
            type: t.section_type, name: t.name, visible: true,
            props: JSON.parse(JSON.stringify(t.props || {})),
        };
        commit([...(sections || []), s]);
        setSelectedId(s.id);
        setShowAdd(false);
        toast.success(`"${t.name}" added — edit the copy here without changing the template`);
    };

    const isGlobalsTab = tab === "header" || tab === "footer";

    const saveDraft = async () => {
        try {
            if (isGlobalsTab) {
                await rhm.put("/site/globals/draft", globals);
                setGlobalsDirty(false);
                qc.invalidateQueries({ queryKey: ["site-globals-crm"] });
            } else {
                await rhm.put(`/site/pages/${slug}/draft`, { sections });
                setDirty(false);
                qc.invalidateQueries({ queryKey: ["site-page", slug] });
            }
            toast.success("Draft saved");
        } catch (e) { toast.error(apiError(e)); }
    };
    const discard = async () => {
        try {
            if (isGlobalsTab) {
                const { data } = await rhm.post("/site/globals/discard");
                setGlobals(data.draft);
                setGlobalsDirty(false);
            } else {
                const { data } = await rhm.post(`/site/pages/${slug}/discard`);
                setSections(data.draft?.sections || []);
                setDirty(false);
                qc.invalidateQueries({ queryKey: ["site-page", slug] });
            }
            toast.success("Draft reset to the live website");
        } catch (e) { toast.error(apiError(e)); }
    };
    const publish = async () => {
        try {
            if (isGlobalsTab) {
                await rhm.put("/site/globals/draft", globals);
                const { data } = await rhm.post("/site/globals/publish");
                setGlobalsDirty(false);
                setShowPublish(false);
                qc.invalidateQueries({ queryKey: ["site-globals-crm"] });
                toast.success(`Header & footer published (v${data.version})`);
                return;
            }
            await rhm.put(`/site/pages/${slug}/draft`, { sections });
            const { data } = await rhm.post(`/site/pages/${slug}/publish`);
            setDirty(false);
            setShowPublish(false);
            toast.success(`Published version ${data.version}`);
            qc.invalidateQueries({ queryKey: ["site-page", slug] });
        } catch (e) { toast.error(apiError(e)); }
    };

    // Ctrl/⌘ + S saves the draft you are working on.
    useSaveShortcut(saveDraft, editable);

    if (!page || sections === null) return <div className="mono text-xs text-zinc-500 p-8">LOADING BUILDER…</div>;    const builderOff = settings && settings.builder_enabled === false;

    return (
        <div data-testid="rhm-website-page">
            <PageHead title="Website Layout" sub="Edit, arrange and publish the public website without touching code" />

            {user?.is_super_admin && (
                <MasterToggle settings={settings} onSaved={() => qc.invalidateQueries({ queryKey: ["site-settings"] })} />
            )}
            {builderOff && !user?.is_super_admin && (
                <Card title="Website layout system is off"><div className="text-sm text-zinc-400">
                    Ask the super admin to switch the website layout system on.</div></Card>
            )}

            <div className="flex flex-wrap gap-1 border-b border-white/10 mb-4">
                {TABS.map((tb) => (
                    <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`website-tab-${tb.key}`}
                        className={`mono text-[10px] tracking-[0.18em] px-4 py-3 border-b-2 transition-colors ${
                            tab === tb.key ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"
                        }`}>{tb.label}</button>
                ))}
            </div>

            {/* toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-bg1 px-4 py-3 mb-4">
                <div className="flex items-center gap-2">
                    {DEVICES.map((d) => (
                        <button key={d.key} onClick={() => setDevice(d.key)} data-testid={`device-${d.key}`}
                            className={`inline-flex items-center gap-2 px-3 py-2 border mono text-[10px] tracking-[0.16em] transition-colors ${
                                device === d.key ? "border-gold text-gold bg-gold/10" : "border-white/15 text-zinc-400 hover:text-white"
                            }`}>
                            <d.icon className="w-3.5 h-3.5" /> {d.label.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span className={`mono text-[10px] tracking-[0.16em] ${(dirty || globalsDirty) ? "text-gold" : "text-zinc-500"}`}
                        data-testid="draft-state">{(dirty || globalsDirty) ? "UNSAVED CHANGES"
                            : (isGlobalsTab ? globalsDoc?.draft_dirty : page.draft_dirty) ? "DRAFT SAVED" : "LIVE = DRAFT"}</span>
                    <Btn variant="ghost" onClick={undo} data-testid="builder-undo"><Undo2 className="w-3.5 h-3.5" /></Btn>
                    <Btn variant="ghost" onClick={redo} data-testid="builder-redo"><Redo2 className="w-3.5 h-3.5" /></Btn>
                    <Btn variant="ghost" onClick={() => setShowVersions(true)} data-testid="builder-versions">
                        <History className="w-3.5 h-3.5 mr-1 inline" /> HISTORY
                    </Btn>
                    <Btn variant="ghost" onClick={discard} data-testid="builder-discard">
                        <RotateCcw className="w-3.5 h-3.5 mr-1 inline" /> RESET
                    </Btn>
                    {editable && <Btn variant="ghost" onClick={saveDraft} data-testid="builder-save-draft">
                        <Save className="w-3.5 h-3.5 mr-1 inline" /> SAVE DRAFT</Btn>}
                    {editable && <span className="mono text-[9px] tracking-[0.16em] text-zinc-600 hidden md:inline"
                        data-testid="builder-save-hint">{saveKeyLabel()} + S</span>}
                    {can("website.publish") && <Btn data-testid="builder-publish"
                        onClick={async () => { await saveDraft(); setShowPublish(true); }}>PUBLISH</Btn>}
                </div>
            </div>

            <div className={`grid gap-4 items-start ${
                ["pages", "banners", "shop"].includes(tab) ? "" : isGlobalsTab ? "lg:grid-cols-2" : "lg:grid-cols-[340px_1fr]"}`}>
                {/* structure + inspector */}
                <div className="space-y-4" style={{ display: tab === "layout" ? undefined : "none" }}>
                    <Card title="Page structure" action={can("website.sections_add") && (
                        <Btn onClick={() => setShowAdd(true)} data-testid="add-section-button">
                            <Plus className="w-3.5 h-3.5 mr-1 inline" /> SECTION
                        </Btn>
                    )}>
                        <div className="space-y-1.5" data-testid="section-tree">
                            {sections.length === 0 && (
                                <div className="text-sm text-zinc-500 py-6 text-center">
                                    No sections yet. Use + SECTION to start building.
                                </div>
                            )}
                            {sections.map((s, i) => (
                                <div key={s.id} draggable={can("website.sections_move")}
                                    onDragStart={(e) => e.dataTransfer.setData("text/plain", s.id)}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => { e.preventDefault(); drop(e.dataTransfer.getData("text/plain"), s.id); }}
                                    onClick={() => setSelectedId(s.id)}
                                    data-testid={`tree-item-${s.id}`}
                                    className={`group flex items-center gap-2 px-3 py-2.5 border cursor-pointer transition-colors ${
                                        selectedId === s.id ? "border-gold bg-gold/[0.07]" : "border-white/10 hover:border-white/30"
                                    } ${s.visible === false ? "opacity-50" : ""}`}>
                                    <Layers className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm truncate">{s.name || REGISTRY[s.type]?.label || s.type}</div>
                                        <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">
                                            {s.visible === false ? "HIDDEN" : "VISIBLE"} · {s.type.toUpperCase()}
                                        </div>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); toggleVisible(s.id); }}
                                        data-testid={`tree-visible-${s.id}`} className="text-zinc-500 hover:text-gold">
                                        {s.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); move(s.id, -1); }} disabled={i === 0}
                                        data-testid={`tree-up-${s.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                                        <ArrowUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); move(s.id, 1); }} disabled={i === sections.length - 1}
                                        data-testid={`tree-down-${s.id}`} className="text-zinc-500 hover:text-white disabled:opacity-30">
                                        <ArrowDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setSaveTemplateFor(s); }}
                                        data-testid={`tree-save-template-${s.id}`} title="Save as template"
                                        className="text-zinc-500 hover:text-gold">
                                        <BookmarkPlus className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); duplicate(s.id); }}
                                        data-testid={`tree-duplicate-${s.id}`} className="text-zinc-500 hover:text-white">
                                        <Copy className="w-3.5 h-3.5" />
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); removeSection(s.id); }}
                                        data-testid={`tree-delete-${s.id}`} className="text-zinc-500 hover:text-red-400">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-white/10">
                            <Select value={slug} data-testid="builder-page-select"
                                onChange={(e) => setSlug(e.target.value)}
                                options={pages.filter((p) => !p.is_archived).map((p) => ({ value: p.slug, label: `${p.name} (/${p.slug})` }))} />
                        </div>
                    </Card>

                    {selected && (
                        <Inspector section={selected} products={products} editable={editable}
                            setProp={setProp} setSection={setSection} />
                    )}
                </div>

                {tab === "header" && globals && (
                    <div className="space-y-4">
                        <HeaderEditor globals={globals} onChange={(g) => { setGlobals(g); setGlobalsDirty(true); }} />
                    </div>
                )}
                {tab === "footer" && globals && (
                    <div className="space-y-4">
                        <FooterEditor globals={globals} onChange={(g) => { setGlobals(g); setGlobalsDirty(true); }} />
                    </div>
                )}
                {tab === "pages" && (
                    <div className="lg:col-span-2">
                        <PagesManager canAdd={can("website.pages_add")} canDelete={can("website.pages_delete")}
                            onEditLayout={(s2) => { setSlug(s2); setTab("layout"); }} />
                    </div>
                )}

                {tab === "banners" && (
                    <div className="lg:col-span-2">
                        <BannerManager canEdit={can("website.edit")} />
                    </div>
                )}

                {tab === "shop" && (
                    <div className="lg:col-span-2">
                        <CollectionsManager editable={editable} />
                    </div>
                )}

                {tab === "product_pages" && <ProductPagesManager editable={editable} />}

                {/* live preview */}
                <div className="border border-white/10 bg-black/40 p-3 lg:sticky lg:top-4 self-start"
                    style={{ display: ["pages", "banners", "shop", "product_pages"].includes(tab) ? "none" : undefined }}>
                    <div className="mx-auto transition-all duration-300 bg-bg0"
                        style={{ width: DEVICES.find((d) => d.key === device).width, maxWidth: "100%" }}>
                        <iframe ref={iframeRef} title="Website preview" data-testid="builder-preview-frame"
                            src={`${window.location.origin}/${slug === "home" ? "" : slug}?builder_preview=1`}
                            className="w-full h-[calc(100vh-260px)] min-h-[560px] bg-bg0" />
                    </div>
                </div>
            </div>

            {inline && (
                <InlineEditor payload={inline} onClose={() => setInline(null)}
                    onSave={(value) => {
                        commit(sections.map((s) => (s.id === inline.sectionId
                            ? { ...s, props: { ...(s.props || {}), [inline.field]: value } } : s)));
                        setInline(null);
                    }} />
            )}
            {showAdd && <AddSection onPick={addSection} onPickTemplate={addFromTemplate}
                onClose={() => setShowAdd(false)} />}
            {saveTemplateFor && <SaveTemplateDialog section={saveTemplateFor}
                onClose={() => setSaveTemplateFor(null)} />}
            {showPublish && <PublishDialog slug={slug} globals={isGlobalsTab}
                onClose={() => setShowPublish(false)} onPublish={publish} />}
            {showVersions && <VersionHistory slug={slug} onClose={() => setShowVersions(false)}
                canRestore={can("website.restore")}
                onRestored={(list) => { setSections(list); setShowVersions(false); }} />}
        </div>
    );
}

// ---------------------------------------------------------------- master toggle
const MasterToggle = ({ settings, onSaved }) => {
    const on = settings?.builder_enabled !== false;
    const flip = async () => {
        try {
            await rhm.put("/site/settings", { builder_enabled: !on });
            toast.success(`Website layout system ${!on ? "switched on" : "switched off"}`);
            onSaved();
        } catch (e) { toast.error(apiError(e)); }
    };
    return (
        <div className={`border p-4 mb-4 flex flex-wrap items-center justify-between gap-4 ${
            on ? "border-white/10 bg-white/[0.02]" : "border-red-400/40 bg-red-500/[0.06]"}`}
            data-testid="website-master-toggle">
            <div>
                <div className="mono text-[10px] tracking-[0.2em] text-zinc-500">WEBSITE LAYOUT SYSTEM · SUPER ADMIN ONLY</div>
                <div className="font-display text-xl tracking-tight mt-1">{on ? "ON" : "OFF"}</div>
                <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                    {on ? "Authorised users can open the visual builder. The public website is unaffected by this switch."
                        : "Only you can open the builder. The public website keeps running on the last published layout."}
                </p>
            </div>
            <button onClick={flip} data-testid="website-master-toggle-button"
                className={`inline-flex items-center gap-2 px-4 py-2.5 border mono text-[10px] tracking-[0.18em] ${
                    on ? "border-gold text-gold hover:bg-gold hover:text-bg0" : "border-red-400 text-red-300 hover:bg-red-400 hover:text-bg0"}`}>
                <Power className="w-3.5 h-3.5" /> {on ? "TURN OFF" : "TURN ON"}
            </button>
        </div>
    );
};

